/**
 * HarbourPage — My Harbour 我的海灣 product landing page.
 *
 * As of M3 (cms_product_pages migration) every block reads from the
 * generated `cmsProductPages` snapshot. The legacy Harbour page used a
 * dozen t-call entries (with the `harbour` namespace and returnObjects)
 * into a 105-leaf i18n JSON; the same data now lives in
 * cms_product_pages.blocks_zhHant JSON so an operator edits it in the
 * admin without touching code.
 *
 * Fallback chain — the page reads cmsProductPages first, but if it's
 * empty (e.g. fresh checkout, sync hasn't run yet) it falls back to the
 * legacy t() reads. This keeps the old behaviour intact until sync + the
 * M3 seed land, then the array-style consumer wins without further
 * code changes.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Container, Reveal, MobileTableCards } from '@/components/ui'
import { H3 } from '@/components/ui/typography'
import { SITE } from '@/content/site'
import SEO from '@/utils/seo'
import { buildBreadcrumbList, buildCourse } from '@/utils/structuredData'
import { cmsProductPages, type CmsProductPageIndexEntry } from '@/content/cms/product-pages.generated'
import {
  fetchLiveProductPage,
  localeOf,
  productPageCopy,
} from '@/content/cms/productPageLocale'

interface BeliefItem { title: string; text: string }
interface FeatureItem { title: string; text: string }
interface HowStep { title: string; text: string }
interface WhoRow { role: string; gets: string }

interface HarbourBlocks {
  hero: { tag: string; title: string; subtitle: string; description: string; ctaPrimary: string; ctaSecondary: string }
  problem: { tag: string; title: string; intro: string; bullets: string[] }
  beliefs: { tag: string; title: string; items: BeliefItem[] }
  design: { tag: string; title: string; intro: string; test: string; closing: string }
  vision: { tag: string; title: string; intro: string; pillars: string[] }
  how: { tag: string; title: string; intro: string; steps: HowStep[] }
  features: { tag: string; title: string; recognition: { title: string; items: FeatureItem[] }; pastoral: { title: string; items: FeatureItem[] } }
  ai: { tag: string; title: string; intro: string; bullets: string[]; gate: string }
  principles: { tag: string; title: string; items: BeliefItem[] }
  who: { tag: string; title: string; intro: string; rows: WhoRow[] }
  northStar: { tag: string; quote: string }
  next: { tag: string; title: string; options: string[]; ctaPrimary: string; ctaSecondary: string }
}

const EMPTY_BLOCKS: HarbourBlocks = {
  hero: { tag: '', title: '', subtitle: '', description: '', ctaPrimary: '', ctaSecondary: '' },
  problem: { tag: '', title: '', intro: '', bullets: [] },
  beliefs: { tag: '', title: '', items: [] },
  design: { tag: '', title: '', intro: '', test: '', closing: '' },
  vision: { tag: '', title: '', intro: '', pillars: [] },
  how: { tag: '', title: '', intro: '', steps: [] },
  features: { tag: '', title: '', recognition: { title: '', items: [] }, pastoral: { title: '', items: [] } },
  ai: { tag: '', title: '', intro: '', bullets: [], gate: '' },
  principles: { tag: '', title: '', items: [] },
  who: { tag: '', title: '', intro: '', rows: [] },
  northStar: { tag: '', quote: '' },
  next: { tag: '', title: '', options: [], ctaPrimary: '', ctaSecondary: '' },
}

/** Fall back to the live API when the generated snapshot is empty (cold
 *  cache, or sync was skipped). The live fetch asks for the ACTIVE locale —
 *  `fetchLiveProductPage` also pulls the zh-Hant copy alongside it and
 *  merges the two into one `locales` map, so the resolver falls back
 *  exactly as it does for the build-time snapshot. */
export default function HarbourPage() {
  const { t, i18n } = useTranslation()
  const locale = localeOf(i18n.language)

  const initial = cmsProductPages.find((p) => p.product_slug === 'harbour') ?? null
  const [page, setPage] = useState<CmsProductPageIndexEntry | null>(initial)
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (initial) return
    void fetchLiveProductPage('harbour', locale).then((live) => {
      if (cancelled) return
      if (live) setPage(live)
      else setMissing(true)
    })
    return () => {
      cancelled = true
    }
  }, [initial, locale])

  // The generated index carries all three locales' copy — resolve the body
  // to the active locale.
  const copy = page ? productPageCopy(page, locale) : null

  // CMS-driven copy, or legacy t() reads when the snapshot is empty.
  // The empty-snapshot branch keeps the page functional before the operator
  // runs `npm run cms:sync` for the first time.
  const blocks: HarbourBlocks = copy
    ? (copy.blocks as unknown as HarbourBlocks)
    : {
        hero: {
          tag: (t('harbour.hero.tag', { defaultValue: '' }) as string) || '',
          title: t('harbour.hero.title') as string,
          subtitle: (t('harbour.hero.subtitle', { defaultValue: '' }) as string) || '',
          description: t('harbour.hero.description') as string,
          ctaPrimary: t('harbour.hero.ctaPrimary') as string,
          ctaSecondary: t('harbour.hero.ctaSecondary') as string,
        },
        problem: {
          tag: t('harbour.problem.tag') as string,
          title: t('harbour.problem.title') as string,
          intro: t('harbour.problem.intro') as string,
          bullets: t('harbour.problem.bullets', { returnObjects: true }) as string[],
        },
        beliefs: {
          tag: t('harbour.beliefs.tag') as string,
          title: t('harbour.beliefs.title') as string,
          items: t('harbour.beliefs.items', { returnObjects: true }) as BeliefItem[],
        },
        design: {
          tag: t('harbour.design.tag') as string,
          title: t('harbour.design.title') as string,
          intro: t('harbour.design.intro') as string,
          test: t('harbour.design.test') as string,
          closing: t('harbour.design.closing') as string,
        },
        vision: {
          tag: t('harbour.vision.tag') as string,
          title: t('harbour.vision.title') as string,
          intro: t('harbour.vision.intro') as string,
          pillars: t('harbour.vision.pillars', { returnObjects: true }) as string[],
        },
        how: {
          tag: t('harbour.how.tag') as string,
          title: t('harbour.how.title') as string,
          intro: t('harbour.how.intro') as string,
          steps: t('harbour.how.steps', { returnObjects: true }) as HowStep[],
        },
        features: {
          tag: t('harbour.features.tag') as string,
          title: t('harbour.features.title') as string,
          recognition: {
            title: t('harbour.features.recognition.title') as string,
            items: t('harbour.features.recognition.items', { returnObjects: true }) as FeatureItem[],
          },
          pastoral: {
            title: t('harbour.features.pastoral.title') as string,
            items: t('harbour.features.pastoral.items', { returnObjects: true }) as FeatureItem[],
          },
        },
        ai: {
          tag: t('harbour.ai.tag') as string,
          title: t('harbour.ai.title') as string,
          intro: t('harbour.ai.intro') as string,
          bullets: t('harbour.ai.bullets', { returnObjects: true }) as string[],
          gate: t('harbour.ai.gate') as string,
        },
        principles: {
          tag: t('harbour.principles.tag') as string,
          title: t('harbour.principles.title') as string,
          items: t('harbour.principles.items', { returnObjects: true }) as BeliefItem[],
        },
        who: {
          tag: t('harbour.who.tag') as string,
          title: t('harbour.who.title') as string,
          intro: t('harbour.who.intro') as string,
          rows: t('harbour.who.rows', { returnObjects: true }) as WhoRow[],
        },
        northStar: {
          tag: t('harbour.northStar.tag') as string,
          quote: t('harbour.northStar.quote') as string,
        },
        next: {
          tag: t('harbour.next.tag') as string,
          title: t('harbour.next.title') as string,
          options: t('harbour.next.options', { returnObjects: true }) as string[],
          ctaPrimary: t('harbour.next.ctaPrimary') as string,
          ctaSecondary: t('harbour.next.ctaSecondary') as string,
        },
      }

  const safe: HarbourBlocks =
    blocks && blocks.hero && blocks.hero.title ? blocks : EMPTY_BLOCKS

  const whatsappHref = `${SITE.contact.whatsapp.link}?text=${encodeURIComponent(safe.hero.title)}`

  return (
    <div className="theme-harbour">
      <SEO
        title={t('seo.harbourTitle')}
        description={t('seo.harbourDescription')}
        keywords={t('seo.harbourKeywords')}
        jsonLd={[
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.products'), path: '/products' },
            { name: 'My Harbour', path: '/products/harbour' },
          ]),
          buildCourse({
            path: '/products/harbour',
            name: 'My Harbour — 品格教育平台',
            description: t('seo.harbourDescription'),
            educationalLevel: 'Secondary',
            keywords: ['品格教育', 'AI 助理', 'Privacy-first', '學生成長', 'YADA'],
          }),
        ]}
      />

      {missing && (
        <section className="py-section-y px-section-x bg-surface">
          <Container size="lg">
            <p className="text-center text-ink-muted">
              尚未在 CMS 建立 Harbour 詳情頁。請先到後台「產品詳情」新增；或執行 <code className="px-1 bg-surface-2 rounded">npm run cms:sync</code>。
            </p>
          </Container>
        </section>
      )}

      {/* 1. Hero — dawn over the water.
          A page about being cared for should not open with the same
          cream gradient every other product page opens with: this one
          gets its own light (warm rose, apricot, and the teal of the
          harbour below), and the one sentence that carries the feeling
          is set in serif, like something said to you. */}
      <section
        className="harbour-hero py-section-y px-section-x"
        aria-labelledby="page-title"
      >
        <div aria-hidden="true" className="harbour-hero__blob harbour-hero__blob--gold" />
        <div aria-hidden="true" className="harbour-hero__blob harbour-hero__blob--rose" />
        <div aria-hidden="true" className="harbour-hero__blob harbour-hero__blob--sea" />

        <Container>
          <Reveal>
            {safe.hero.tag && (
              <span className="section-title__tagline inline-block mb-4">{safe.hero.tag}</span>
            )}
            <h1
              id="page-title"
              className="hero-title text-3xl md:text-4xl lg:text-5xl text-ink max-w-4xl"
            >
              {safe.hero.title}
            </h1>
            {safe.hero.description && (
              <p className="mt-5 text-lg md:text-xl text-ink-soft max-w-3xl leading-relaxed">
                {safe.hero.description}
              </p>
            )}
            {safe.hero.subtitle && (
              <p className="harbour-lede mt-6 max-w-2xl">{safe.hero.subtitle}</p>
            )}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a
                href="#next-section"
                className="inline-flex items-center gap-2 bg-primary text-surface px-7 py-3 rounded-yada-full font-semibold shadow-yada-md hover:bg-primary-dark transition-colors"
              >
                {safe.hero.ctaPrimary}
                <Icon icon="lucide:arrow-right" />
              </a>
              <a
                href="#how-section"
                className="inline-flex items-center gap-2 bg-transparent border-2 border-primary text-primary px-7 py-3 rounded-yada-full font-semibold hover:bg-primary hover:text-surface transition-colors"
              >
                {safe.hero.ctaSecondary}
              </a>
            </div>
          </Reveal>
        </Container>

        {/* The shoreline: the page steps down onto the sand. */}
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 text-surface-2">
          <svg
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            className="block w-full h-[clamp(2.5rem,6vw,5rem)]"
          >
            <path
              fill="currentColor"
              opacity="0.5"
              d="M0,64 C160,124 320,4 700,44 C1080,84 1240,124 1440,72 L1440,120 L0,120 Z"
            />
            <path
              fill="currentColor"
              d="M0,86 C200,140 420,20 720,54 C1000,86 1220,132 1440,92 L1440,120 L0,120 Z"
            />
          </svg>
        </div>
      </section>

      {/* 2. The problem */}
      <section id="problem-section" className="py-section-y px-section-x bg-surface-2" aria-labelledby="problem-title">
        <Container>
          <Reveal>
            <div className="text-center mb-8 max-w-3xl mx-auto">
              <span className="section-title__tagline inline-block mb-3">{safe.problem.tag}</span>
              <h2 id="problem-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.problem.title}</h2>
              <p className="mt-4 text-lg text-ink-soft leading-relaxed">{safe.problem.intro}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {safe.problem.bullets.map((bullet, idx) => (
              <Reveal key={idx} delay={idx * 0.05} className="h-full">
                <div className="harbour-card flex items-start gap-3 p-4 h-full">
                  <span className="w-7 h-7 rounded-full bg-danger/10 text-danger flex items-center justify-center flex-shrink-0">
                    <Icon icon="lucide:alert-triangle" className="text-base" />
                  </span>
                  <p className="text-ink-soft leading-relaxed">{bullet}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 3. Our beliefs */}
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="beliefs-title">
        <Container>
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{safe.beliefs.tag}</span>
              <h2 id="beliefs-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.beliefs.title}</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-gap-cards">
            {safe.beliefs.items.map((item, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div className="harbour-card h-full p-5 md:p-6">
                  <div className="harbour-badge w-12 h-12 mb-4">
                    <Icon icon="lucide:heart" className="text-2xl" />
                  </div>
                  <H3 className="mb-2 leading-snug">{item.title}</H3>
                  <p className="text-sm text-ink-soft leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 4. Design philosophy */}
      <section className="py-section-y px-section-x bg-surface-2" aria-labelledby="design-title">
        <Container size="lg">
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{safe.design.tag}</span>
              <h2 id="design-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.design.title}</h2>
            </div>
            <p className="text-center text-lg text-ink-soft max-w-3xl mx-auto leading-relaxed">{safe.design.intro}</p>
            {safe.design.test && (
              <div className="harbour-quote-panel mt-8 max-w-3xl mx-auto text-center">
                <blockquote className="harbour-quote text-xl md:text-2xl leading-relaxed font-semibold">
                  「{safe.design.test}」
                </blockquote>
              </div>
            )}
            {safe.design.closing && (
              <p className="mt-6 text-center text-lg text-ink-soft max-w-2xl mx-auto leading-relaxed">{safe.design.closing}</p>
            )}
          </Reveal>
        </Container>
      </section>

      {/* 5. Vision */}
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="vision-title">
        <Container>
          <Reveal>
            <div className="text-center mb-8 max-w-3xl mx-auto">
              <span className="section-title__tagline inline-block mb-3">{safe.vision.tag}</span>
              <h2 id="vision-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.vision.title}</h2>
              <p className="mt-4 text-lg text-ink-soft leading-relaxed">{safe.vision.intro}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {safe.vision.pillars.map((pillar, idx) => (
              <Reveal key={idx} delay={idx * 0.08} className="h-full">
                <div className="harbour-card flex items-start gap-3 p-5 h-full">
                  <span className="harbour-badge harbour-badge--sea w-9 h-9 rounded-full font-bold flex-shrink-0">
                    {idx + 1}
                  </span>
                  <p className="text-ink leading-relaxed font-medium">{pillar}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 6. How it works */}
      <section id="how-section" className="py-section-y px-section-x bg-surface-2" aria-labelledby="how-title">
        <Container>
          <Reveal>
            <div className="text-center mb-8 max-w-3xl mx-auto">
              <span className="section-title__tagline inline-block mb-3">{safe.how.tag}</span>
              <h2 id="how-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.how.title}</h2>
              <p className="mt-4 text-lg text-ink-soft leading-relaxed">{safe.how.intro}</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-gap-cards">
            {safe.how.steps.map((step, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div className="harbour-card h-full p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="harbour-step__num">{idx + 1}</span>
                    <H3 className="leading-snug">{step.title}</H3>
                  </div>
                  <p className="text-sm text-ink-soft leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 7. Features (recognition + pastoral) */}
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="features-title">
        <Container>
          <Reveal>
            <div className="text-center mb-10">
              <span className="section-title__tagline inline-block mb-3">{safe.features.tag}</span>
              <h2 id="features-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.features.title}</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {([
              { title: safe.features.recognition.title, items: safe.features.recognition.items, icon: 'lucide:award' },
              { title: safe.features.pastoral.title, items: safe.features.pastoral.items, icon: 'lucide:heart-handshake' },
            ] as const).map((col, idx) => (
              <div key={idx}>
                <h3 className="text-xl md:text-2xl font-bold text-ink mb-4 flex items-center gap-2">
                  <Icon icon={col.icon} className="text-primary" />
                  {col.title}
                </h3>
                <ul className="space-y-3">
                  {col.items.map((item, jdx) => (
                    <Reveal key={jdx} delay={jdx * 0.05}>
                      <li className="harbour-card flex items-start gap-3 p-4">
                        <Icon icon="lucide:check-circle" className="text-primary mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-bold text-ink leading-snug">{item.title}</h4>
                          <p className="text-sm text-ink-soft leading-relaxed">{item.text}</p>
                        </div>
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* 8. AI section */}
      <section className="py-section-y px-section-x bg-surface-2" aria-labelledby="ai-title">
        <Container>
          <Reveal>
            <div className="text-center mb-8 max-w-3xl mx-auto">
              <span className="section-title__tagline inline-block mb-3">{safe.ai.tag}</span>
              <h2 id="ai-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.ai.title}</h2>
              <p className="mt-4 text-lg text-ink-soft leading-relaxed">{safe.ai.intro}</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {safe.ai.bullets.map((bullet, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div className="harbour-card flex items-start gap-2 p-4 h-full">
                  <Icon icon="lucide:sparkles" className="text-sea mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-ink-soft leading-relaxed">{bullet}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {safe.ai.gate && (
            <p className="mt-8 text-center text-base italic text-ink-muted max-w-2xl mx-auto leading-relaxed">
              {safe.ai.gate}
            </p>
          )}
        </Container>
      </section>

      {/* 9. Principles */}
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="principles-title">
        <Container>
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{safe.principles.tag}</span>
              <h2 id="principles-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.principles.title}</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-gap-cards max-w-4xl mx-auto">
            {safe.principles.items.map((item, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div className="harbour-card h-full p-5 md:p-6">
                  <div className="harbour-badge harbour-badge--sea w-12 h-12 mb-4">
                    <Icon icon="lucide:shield-check" className="text-2xl" />
                  </div>
                  <H3 className="mb-2 leading-snug">{item.title}</H3>
                  <p className="text-sm text-ink-soft leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 10. Who it's for */}
      <section className="py-section-y px-section-x bg-surface-2" aria-labelledby="who-title">
        <Container size="lg">
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{safe.who.tag}</span>
              <h2 id="who-title" className="section-title__title text-3xl md:text-4xl text-ink">{safe.who.title}</h2>
              <p className="mt-4 text-lg text-ink-soft max-w-3xl mx-auto leading-relaxed">{safe.who.intro}</p>
            </div>
          </Reveal>
          <Reveal>
            <div className="max-w-4xl mx-auto harbour-dots rounded-yada-2xl p-4 md:p-6">
              <div className="harbour-card p-0 overflow-hidden">
                <MobileTableCards
                  data={safe.who.rows.map((r, i) => ({ ...r, _idx: i }))}
                  rowKey="_idx"
                  columns={[
                    { header: safe.who.tag, field: 'role' },
                    { header: safe.who.title, field: 'gets' },
                  ]}
                />
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* 11. North star — rose dusk over the harbour.
          The one sentence the whole product exists to make possible
          gets its own light: a deepened rose panel with warm light
          lettering, rather than a line of text on cream. */}
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="north-star-title">
        <Container size="lg">
          <Reveal>
            <div className="harbour-north rounded-yada-2xl px-6 py-10 md:px-12 md:py-14 text-center shadow-yada-lg">
              <span className="inline-block text-accent-soft text-sm font-bold uppercase tracking-[0.2em] mb-4">
                {safe.northStar.tag}
              </span>
              {safe.northStar.quote && (
                <blockquote className="harbour-north__quote text-xl md:text-3xl leading-relaxed font-bold max-w-3xl mx-auto">
                  「{safe.northStar.quote}」
                </blockquote>
              )}
            </div>
          </Reveal>
        </Container>
      </section>

      {/* 12. Next step — the light going out of the water */}
      <section id="next-section" className="harbour-cta py-section-y px-section-x text-surface">
        <Container>
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="inline-block text-accent-soft text-sm font-bold uppercase tracking-[0.2em] mb-3">
                {safe.next.tag}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-surface">{safe.next.title}</h2>
              <div className="grid sm:grid-cols-3 gap-3 mb-8">
                {safe.next.options.map((opt, idx) => (
                  <div key={idx} className="bg-surface/12 backdrop-blur border border-surface/25 rounded-yada-lg p-3 text-sm font-semibold text-surface">
                    {opt}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent text-ink px-7 py-3 rounded-yada-full font-semibold shadow-yada-md hover:bg-accent-soft transition-colors"
                >
                  <Icon icon="lucide:message-circle" />
                  {safe.next.ctaPrimary}
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-surface text-surface px-7 py-3 rounded-yada-full font-semibold hover:bg-surface hover:text-primary-dark transition-colors"
                >
                  {safe.next.ctaSecondary}
                  <Icon icon="lucide:arrow-right" />
                </a>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Back to Products button */}
      <section className="py-section-y px-section-x bg-surface">
        <Container>
          <div className="text-center">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-ink-soft hover:text-primary transition-colors font-medium focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
              <span>{t('products.backToProducts')}</span>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  )
}
